import {Square} from './model/Square';
import {logUtishaDebug, logUtishaError} from 'src/app/util/log';
import {AudioSource} from '../model/AudioSource';
import {CpuPressureLevel} from '../model/PerformanceMonitor';

export const defaultTimerDimensions: TimerOptions = {
  timeMs: 20000,
  dimensions: new Square({x: 0, y: 0}, 300),
  ringWidth: 45,
  beepOn: [1, 2, 3]
};

export type TimerOptions = {
  timeMs?: number
  dimensions?: Square
  ringWidth?: number,
  beepOn?: number[],
  beepAudioSource?: AudioSource
  onStopped?: (timer: Timer) => void
}

// see https://codepen.io/onge/pen/JoYEZo

const beepAudio = new Audio('/assets/audio/beep1.wav');
beepAudio.load();

const beepLongAudio = new Audio('/assets/audio/beep_long1.wav');
beepLongAudio.load();

export class Timer {
  private timerStart: number;
  private timerOffset: number;
  private angle: number;
  private timeoutHandler: any;
  private beepsHandled: number[] = [];
  cpuPressure: CpuPressureLevel = 'normal';
  private animationHandle: number;
  private startFontSize: number;
  private targetFontSize: number;
  private currentFrame?: number = undefined;
  private animationSteps = 50;
  private _lastTimeUpdate = 0;
  private _lastDisplayedSeconds = 0;

  /**
   * Calculates the optimal ring width based on timer dimensions and font size
   * to ensure the ring envelops the text without overlapping it
   */
  private calculateOptimalRingWidth(): number {
    if (!this.options.dimensions) {
      return 5; // Default fallback
    }

    const timerWidth = this.options.dimensions.width;

    // For very small timers, use a minimal ring width
    if (timerWidth < 40) {
      return Math.max(1, Math.floor(timerWidth * 0.1)); // 10% of width but at least 1px
    }

    const fontSize = this.targetFontSize; // Use target (smaller) font size for calculation

    // Calculate text width more accurately by measuring it
    let textWidth: number;
    const ctx = this.canvas.getContext('2d');
    if (ctx) {
      // Set font to match what will be used for display
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      // Get max expected seconds (e.g., "20" for 20 seconds)
      const maxSecondsText = Math.ceil((this.options.timeMs || 20000) / 1000).toString();
      // Measure the text width
      textWidth = ctx.measureText(maxSecondsText).width;
    } else {
      // Fallback if context not available
      textWidth = fontSize * (this.options.timeMs && this.options.timeMs >= 10000 ? 1.2 : 0.8);
    }

    // Add padding around text (150% of text width to account for shadows/outlines)
    const textWithPadding = textWidth * 1.5;

    // Calculate available space between text and timer edge
    const availableSpace = (timerWidth / 2) - (textWithPadding / 2);

    // Check if we have any meaningful space for a ring
    if (availableSpace <= 0) {
      // For extremely small timers or large text, use minimal ring
      return Math.max(1, Math.floor(timerWidth * 0.05)); // 5% of width, minimum 1px
    }

    // Calculate ring width to be 40-50% of available space, based on timer size
    // Smaller timers need proportionally smaller rings for visibility
    const ringWidthRatio = timerWidth < 60 ? 0.25 : timerWidth < 150 ? 0.4 : 0.5;

    // Ensure ring width is reasonable: at least 1px, at most 15% of timer width
    return Math.max(1, Math.min(Math.floor(availableSpace * ringWidthRatio), Math.floor(timerWidth * 0.15)));
  }

  constructor(public canvas: HTMLCanvasElement, public options: TimerOptions) {
    this.options = Object.assign({}, defaultTimerDimensions, options);
    this.timerStart = new Date().getTime();
    this.timerOffset = this.options.timeMs || 0;

    // Calculate font sizes based on timer dimensions
    const timerWidth = this.options.dimensions?.width || 300;

    // For very small timers, use more conservative font sizing
    if (timerWidth < 60) {
      // For tiny timers, make sure text is readable but doesn't overflow
      this.startFontSize = Math.max(8, Math.floor(timerWidth * 0.25)); // Maximum 25% of width, minimum 8px
      this.targetFontSize = Math.max(8, Math.floor(timerWidth * 0.25)); // Same size for small timers
    } else {
      this.startFontSize = Math.floor(timerWidth * 0.32); // ~32% of width for start size
      this.targetFontSize = Math.floor(timerWidth * 0.16); // ~16% of width for target size
    }

    // If ringWidth is not explicitly provided, calculate optimal width
    if (this.options.ringWidth === undefined) {
      this.options.ringWidth = this.calculateOptimalRingWidth();
    }

    logUtishaDebug(`Timer created with ${this.timerOffset}ms duration at position (${this.options.dimensions?.topLeft.x}, ${this.options.dimensions?.topLeft.y})`);

    this._lastTimeUpdate = performance.now();
    this._lastDisplayedSeconds = Math.round(this.timerOffset / 1000);
    this.animationHandle = window.requestAnimationFrame(() => this.countDownClock());
  }

  stop() {
    logUtishaDebug('Timer stopping');
    if (this.timeoutHandler) {
      clearTimeout(this.timeoutHandler);
    }
    if (typeof this.animationHandle !== 'undefined') {
      window.cancelAnimationFrame(this.animationHandle);
    }
    if (this.options.onStopped) {
      this.options.onStopped(this);
    }
  }

  /**
   * Manually render the timer on demand
   * This allows external components to force a timer render
   * without waiting for the animation frame
   */
  render() {
    // Ensure currentFrame is initialized before rendering
    if (typeof this.currentFrame === 'undefined') {
      this.currentFrame = 0;
    }

    // Set flag indicating we're being rendered externally
    this.isExternallyRendered = true;

    // Reuse the countdown clock logic but without scheduling a new frame
    this.renderTimerFrame();
  }

  /**
   * Set whether this timer is being rendered by an external component
   * When true, the timer will not request its own animation frames
   */
  setExternallyRendered(value: boolean) {
    this.isExternallyRendered = value;
  }

  // Cache for font sizes to avoid recalculating on every frame
  private fontSizeCache: Map<number, number> = new Map();

  getFontSize() {
    // Initialize currentFrame if it's undefined
    if (typeof this.currentFrame === 'undefined') {
      this.currentFrame = 0;
      return this.startFontSize;
    }
    if (this.currentFrame > this.animationSteps) {
      return this.targetFontSize;
    }

    // Skip easing animation under CPU pressure — return target size instantly
    if (this.cpuPressure !== 'normal') {
      return this.targetFontSize;
    }
    // Check if we have a cached value for this frame
    const cachedSize = this.fontSizeCache.get(this.currentFrame);
    if (cachedSize !== undefined) {
      return cachedSize;
    }
    let currentProgress = this.currentFrame;
    const start = this.startFontSize;
    const distance = this.targetFontSize - start;
    const steps = this.animationSteps;
    currentProgress /= steps / 2;
    let fontSize;
    if (currentProgress < 1) {
      fontSize = (distance / 2) * (Math.pow(currentProgress, 3)) + start;
    } else {
      currentProgress -= 2;
      fontSize = distance / 2 * (Math.pow(currentProgress, 3) + 2) + start;
    }
    // Cache the calculated font size
    this.fontSizeCache.set(this.currentFrame, fontSize);
    return fontSize;
  }


// Change timer face colour
  colourChanger(intAngle: number) {
    // RGB values
    // Green: 	 51 153  0
    // Orange:	244 138  0
    // Red:		255   0  0

    intAngle = 6.29 - intAngle;

    if (Math.floor(72 + 55 * intAngle) < 255 || Math.floor(214 + 14 * intAngle) < 255) {
      // Animate from green to amber
      return 'rgb(' + Math.floor(72 + 55 * intAngle) + ',' + Math.floor(214 + 14 * intAngle) + ',0)';
    } else {
      // Animate from amber to red
      return 'rgb(' + Math.floor(255) + ',' + Math.floor(597 - (90 * intAngle)) + ',0)';
    }
  }

  private beep() {
    const {beepAudioSource, beepOn} = this.options;
    if (!beepAudioSource || !beepOn || beepOn.length === 0) {
      logUtishaDebug('No beep configured');
      return;
    }
    const beepToBePlayed = beepOn.find((beep) => this.displayedTimeSeconds <= beep && this.beepsHandled.indexOf(beep) === -1);
    if (beepToBePlayed) {
      logUtishaDebug(`Playing beep at ${beepToBePlayed} seconds`);
      this.beepsHandled.push(beepToBePlayed);
      setTimeout(() => {
        try {
          beepAudioSource.play();
        } catch (e: any) {
          // the first playback can be a bit slow, then the second playback can overlap, causing an error on some browsers
          logUtishaError('Error playing beep: ', e);
        }
      }, 150);
      return;
    }
  }

  private beepLong() {
    setTimeout(() => beepLongAudio.play(), 0);
  }

  get displayedTimeSeconds() {
    // Use cached value if available, otherwise calculate
    return this._lastDisplayedSeconds !== undefined ? this._lastDisplayedSeconds : Math.round(this.timerOffset / 1000);
  }

  // Update renderTimerFrame method in timer.ts
  private renderTimerFrame() {
    if (!this.options.timeMs || !this.options.dimensions) {
      throw new Error('Timer not configured properly');
    }

    // Ensure currentFrame is initialized
    if (typeof this.currentFrame === 'undefined') {
      this.currentFrame = 0;
    }

    // Calculate current timer state
    const now = new Date().getTime();
    this.timerOffset = Math.max(0, this.options.timeMs - (now - this.timerStart));
    this.angle = (2 * Math.PI / (this.options.timeMs * 0.001)) * 0.001 * this.timerOffset;

    // Render the timer on canvas
    if (this.canvas.getContext) {
      const ctx = this.canvas.getContext('2d');
      if (ctx === null) {
        throw new Error('Could not get canvas context');
      }

      const {topLeft: {x, y}, width} = this.options.dimensions;

      // Save context state
      ctx.save();

      // Clear timer area before re-drawing
      // this.clear(ctx);

      // Calculate radius dynamically based on ring width to ensure it envelops text without overlapping
      const ringWidth = this.options.ringWidth || this.calculateOptimalRingWidth();
      // Ensure radius is always positive by limiting ring width based on available space
      const maxPossibleRingWidth = Math.max(1, width / 2 - 6); // Allow at least 1px for radius
      const adjustedRingWidth = Math.min(ringWidth, maxPossibleRingWidth);
      const radius = Math.max(1, (width / 2) - (adjustedRingWidth / 2) - 5); // Ensure at least 1px radius
      const centerX = x + width / 2;
      const centerY = y + width / 2;

      // Draw background circle for timer
      // ctx.beginPath();
      // ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      // ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      // ctx.fill();

      // Draw progress ring
      if (this.timerOffset > 0) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, -Math.PI / 2, -Math.PI / 2 + this.angle);
        ctx.strokeStyle = this.colourChanger(this.angle);
        ctx.lineWidth = adjustedRingWidth; // Use the adjusted ring width to prevent overflow
        ctx.stroke();
      }

      // Enhanced text rendering — skip shadows under minimal CPU pressure
      if (this.cpuPressure === 'minimal') {
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      } else {
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        ctx.shadowBlur = 2;
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
      }

      const fontSize = this.getFontSize();
      ctx.font = `bold ${fontSize}px Arial, sans-serif`; // Use a more common font as fallback
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const seconds = Math.ceil(this.timerOffset / 1000); // Use ceil instead of round for countdown feel
      if (seconds > 0) {
        this._lastDisplayedSeconds = seconds;
        const text = seconds.toString();
        // Draw text with outline for better visibility
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.strokeText(text, centerX, centerY);
        ctx.fillText(text, centerX, centerY);

        // Play beep sound if needed
        this.beep();
      }

      // Restore context state
      ctx.restore();
    }

    // Check if timer is complete
    if (this.timerOffset <= 0) {
      this.stop();
    }
  }

  // Build our countdown clock
  countDownClock() {
    if (typeof this.currentFrame === 'undefined') {
      this.currentFrame = 0;
    } else {
      this.currentFrame++;
    }

    // Render the current frame
    this.renderTimerFrame();

    // Only schedule next frame if we're not being rendered by a parent component
    // This avoids duplicate animation frames when timer is drawn by CanvasRenderingPipeline
    if (this.timerOffset > 0 && !this.isExternallyRendered) {
      this.animationHandle = window.requestAnimationFrame(() => this.countDownClock());
    }
  }

  // Flag to indicate if timer is being rendered by an external component
  private isExternallyRendered = false;


}
